import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { FiEdit, FiMenu, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import SaktiSidebar from '../components/SaktiSidebar';
import { useSaktiAppConfig } from '../hooks/useSaktiAppConfig';
import {
  CreateAppConfigPayload,
  CreateFeatureFlagPayload,
  SaktiAppConfigItem,
  SaktiFeatureFlagAdminItem,
} from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

// ─── App Config Form Modal ─────────────────────────────────────────────────────

const EMPTY_CONFIG: CreateAppConfigPayload = {
  platform: 'android',
  company_id: null,
  estate_id: null,
  latest_version_code: 0,
  latest_version_name: null,
  min_supported_version_code: 0,
  force_update: false,
  update_message: null,
  store_url: null,
  is_active: true,
};

interface AppConfigFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SaktiAppConfigItem | null;
  onSave: (payload: CreateAppConfigPayload) => Promise<boolean>;
  saving: boolean;
}

const AppConfigFormModal: React.FC<AppConfigFormModalProps> = ({ isOpen, onClose, item, onSave, saving }) => {
  const [form, setForm] = useState<CreateAppConfigPayload>(EMPTY_CONFIG);

  useEffect(() => {
    if (isOpen) {
      setForm(item ? {
        platform: item.platform,
        company_id: item.company_id,
        estate_id: item.estate_id,
        latest_version_code: item.latest_version_code,
        latest_version_name: item.latest_version_name,
        min_supported_version_code: item.min_supported_version_code,
        force_update: item.force_update,
        update_message: item.update_message,
        store_url: item.store_url,
        is_active: item.is_active,
      } : EMPTY_CONFIG);
    }
  }, [isOpen, item]);

  const handleSubmit = async () => {
    const ok = await onSave(form);
    if (ok) onClose();
  };

  const set = (patch: Partial<CreateAppConfigPayload>) => setForm(f => ({ ...f, ...patch }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="customGreen.500" color="white" borderTopRadius="md" fontSize={SAKTI_UI_SIZE.modalHeaderFont}>
          {item ? 'Edit App Config' : 'Tambah App Config'}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={4}>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Platform</FormLabel>
              <Select value={form.platform} onChange={e => set({ platform: e.target.value })}>
                <option value="android">Android</option>
                <option value="ios">iOS</option>
              </Select>
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Company ID (opsional)</FormLabel>
                <Input
                  type="number"
                  value={form.company_id ?? ''}
                  onChange={e => set({ company_id: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Kosong = global"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Estate ID (opsional)</FormLabel>
                <Input
                  type="number"
                  value={form.estate_id ?? ''}
                  onChange={e => set({ estate_id: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Kosong = global"
                />
              </FormControl>
            </HStack>
            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Latest Version Code</FormLabel>
                <Input
                  type="number"
                  value={form.latest_version_code}
                  onChange={e => set({ latest_version_code: Number(e.target.value) })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Latest Version Name</FormLabel>
                <Input
                  value={form.latest_version_name ?? ''}
                  onChange={e => set({ latest_version_name: e.target.value || null })}
                  placeholder="e.g. 1.0.0"
                />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm">Min Supported Version Code</FormLabel>
              <Input
                type="number"
                value={form.min_supported_version_code}
                onChange={e => set({ min_supported_version_code: Number(e.target.value) })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Update Message (opsional)</FormLabel>
              <Textarea
                value={form.update_message ?? ''}
                onChange={e => set({ update_message: e.target.value || null })}
                rows={2}
                placeholder="Pesan yang ditampilkan ke pengguna"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Store URL (opsional)</FormLabel>
              <Input
                value={form.store_url ?? ''}
                onChange={e => set({ store_url: e.target.value || null })}
                placeholder="https://play.google.com/..."
              />
            </FormControl>
            <HStack justify="space-between" pt={1}>
              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Force Update</FormLabel>
                <Switch
                  colorScheme="red"
                  isChecked={form.force_update}
                  onChange={e => set({ force_update: e.target.checked })}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Aktif</FormLabel>
                <Switch
                  colorScheme="green"
                  isChecked={form.is_active}
                  onChange={e => set({ is_active: e.target.checked })}
                />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose} isDisabled={saving}>Batal</Button>
          <Button colorScheme="green" isLoading={saving} onClick={handleSubmit}>
            {item ? 'Update' : 'Simpan'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─── Feature Flag Form Modal ───────────────────────────────────────────────────

const EMPTY_FLAG = {
  flag_key: '',
  company_id: null as number | null,
  estate_id: null as number | null,
  is_enabled: false,
  description: null as string | null,
  is_active: true,
};

interface FeatureFlagFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: SaktiFeatureFlagAdminItem | null;
  onSave: (payload: CreateFeatureFlagPayload) => Promise<boolean>;
  saving: boolean;
}

const FeatureFlagFormModal: React.FC<FeatureFlagFormModalProps> = ({ isOpen, onClose, item, onSave, saving }) => {
  const [form, setForm] = useState(EMPTY_FLAG);
  const [valueText, setValueText] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setForm({
          flag_key: item.flag_key,
          company_id: item.company_id,
          estate_id: item.estate_id,
          is_enabled: item.is_enabled,
          description: item.description,
          is_active: item.is_active,
        });
        setValueText(item.value != null ? JSON.stringify(item.value, null, 2) : '');
      } else {
        setForm(EMPTY_FLAG);
        setValueText('');
      }
    }
  }, [isOpen, item]);

  const handleSubmit = async () => {
    let parsedValue: unknown = null;
    if (valueText.trim()) {
      try { parsedValue = JSON.parse(valueText); }
      catch { parsedValue = valueText; }
    }
    const ok = await onSave({ ...form, value: parsedValue });
    if (ok) onClose();
  };

  const set = (patch: Partial<typeof EMPTY_FLAG>) => setForm(f => ({ ...f, ...patch }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="customGreen.500" color="white" borderTopRadius="md" fontSize={SAKTI_UI_SIZE.modalHeaderFont}>
          {item ? 'Edit Feature Flag' : 'Tambah Feature Flag'}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={4}>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Flag Key</FormLabel>
              <Input
                value={form.flag_key}
                onChange={e => set({ flag_key: e.target.value })}
                placeholder="e.g. enable_new_feature"
              />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel fontSize="sm">Company ID (opsional)</FormLabel>
                <Input
                  type="number"
                  value={form.company_id ?? ''}
                  onChange={e => set({ company_id: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Kosong = global"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Estate ID (opsional)</FormLabel>
                <Input
                  type="number"
                  value={form.estate_id ?? ''}
                  onChange={e => set({ estate_id: e.target.value ? Number(e.target.value) : null })}
                  placeholder="Kosong = global"
                />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm">Deskripsi (opsional)</FormLabel>
              <Textarea
                value={form.description ?? ''}
                onChange={e => set({ description: e.target.value || null })}
                rows={2}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Value JSON (opsional)</FormLabel>
              <Textarea
                value={valueText}
                onChange={e => setValueText(e.target.value)}
                placeholder='{"threshold": 100}'
                rows={3}
                fontFamily="mono"
                fontSize="sm"
              />
            </FormControl>
            <HStack justify="space-between" pt={1}>
              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Is Enabled</FormLabel>
                <Switch
                  colorScheme="blue"
                  isChecked={form.is_enabled}
                  onChange={e => set({ is_enabled: e.target.checked })}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center" gap={3}>
                <FormLabel mb={0} fontSize="sm">Aktif</FormLabel>
                <Switch
                  colorScheme="green"
                  isChecked={form.is_active}
                  onChange={e => set({ is_active: e.target.checked })}
                />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose} isDisabled={saving}>Batal</Button>
          <Button colorScheme="green" isLoading={saving} onClick={handleSubmit}>
            {item ? 'Update' : 'Simpan'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const SaktiAppConfigPage: React.FC = () => {
  const {
    appConfigs,
    featureFlags,
    loading,
    fetchAll,
    createConfig,
    updateConfig,
    deleteConfig,
    createFlag,
    updateFlag,
    deleteFlag,
  } = useSaktiAppConfig();

  const { isOpen: isMobileOpen, onOpen: onMobileOpen, onClose: onMobileClose } = useDisclosure();
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure();
  const { isOpen: isFlagOpen, onOpen: onFlagOpen, onClose: onFlagClose } = useDisclosure();

  const [editConfig, setEditConfig] = useState<SaktiAppConfigItem | null>(null);
  const [editFlag, setEditFlag] = useState<SaktiFeatureFlagAdminItem | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingFlag, setSavingFlag] = useState(false);

  const handleOpenCreateConfig = () => { setEditConfig(null); onConfigOpen(); };
  const handleEditConfig = (cfg: SaktiAppConfigItem) => { setEditConfig(cfg); onConfigOpen(); };
  const handleSaveConfig = async (payload: CreateAppConfigPayload) => {
    setSavingConfig(true);
    const ok = editConfig
      ? await updateConfig(editConfig.id, payload)
      : await createConfig(payload);
    setSavingConfig(false);
    return ok;
  };
  const handleDeleteConfig = async (id: string) => {
    if (window.confirm('Hapus app config ini?')) await deleteConfig(id);
  };

  const handleOpenCreateFlag = () => { setEditFlag(null); onFlagOpen(); };
  const handleEditFlag = (flag: SaktiFeatureFlagAdminItem) => { setEditFlag(flag); onFlagOpen(); };
  const handleSaveFlag = async (payload: CreateFeatureFlagPayload) => {
    setSavingFlag(true);
    const ok = editFlag
      ? await updateFlag(editFlag.id, payload)
      : await createFlag(payload);
    setSavingFlag(false);
    return ok;
  };
  const handleDeleteFlag = async (id: string) => {
    if (window.confirm('Hapus feature flag ini?')) await deleteFlag(id);
  };

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="app-config" />
        </Box>

        <Drawer isOpen={isMobileOpen} placement="left" onClose={onMobileClose} size="xs">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="app-config" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>
            <HStack justify="space-between">
              <HStack spacing={3} align="center">
                <IconButton
                  aria-label="Open menu"
                  icon={<FiMenu />}
                  variant="outline"
                  bg="white"
                  display={{ base: 'flex', xl: 'none' }}
                  onClick={onMobileOpen}
                />
                <VStack align="start" spacing={0}>
                  <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                    App Config
                  </Text>
                  <Text color="gray.600" fontSize={SAKTI_UI_SIZE.pageSubtitle}>
                    Konfigurasi versi aplikasi mobile dan feature flags
                  </Text>
                </VStack>
              </HStack>
              <IconButton
                aria-label="Refresh"
                icon={<FiRefreshCw />}
                variant="outline"
                bg="white"
                isLoading={loading}
                onClick={fetchAll}
              />
            </HStack>

            <Box bg="white" borderRadius={SAKTI_UI_SIZE.cardRadius} borderWidth="1px" borderColor="gray.200" overflow="hidden">
              <Tabs colorScheme="green">
                <TabList px={4} pt={3} borderBottomWidth="1px" borderColor="gray.200">
                  <Tab fontSize="sm" fontWeight="semibold">App Config</Tab>
                  <Tab fontSize="sm" fontWeight="semibold">Feature Flags</Tab>
                </TabList>

                <TabPanels>
                  {/* App Config */}
                  <TabPanel p={4}>
                    <HStack justify="flex-end" mb={3}>
                      <Button leftIcon={<FiPlus />} colorScheme="green" size="sm" onClick={handleOpenCreateConfig}>
                        Tambah Config
                      </Button>
                    </HStack>
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Platform</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Company</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Estate</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Latest Ver</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Min Ver</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Force Update</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Status</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Aksi</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {loading ? (
                            <Tr><Td colSpan={8} textAlign="center" py={8}><Spinner size="sm" /></Td></Tr>
                          ) : appConfigs.length === 0 ? (
                            <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400" fontSize="sm">Belum ada data app config</Td></Tr>
                          ) : appConfigs.map(cfg => (
                            <Tr key={cfg.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                <Badge colorScheme={cfg.platform === 'android' ? 'green' : 'blue'}>{cfg.platform}</Badge>
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{cfg.company_id ?? '—'}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{cfg.estate_id ?? '—'}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>
                                {cfg.latest_version_code}
                                {cfg.latest_version_name && <Text as="span" color="gray.500"> ({cfg.latest_version_name})</Text>}
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{cfg.min_supported_version_code}</Td>
                              <Td>
                                {cfg.force_update
                                  ? <Badge colorScheme="red">Force Update</Badge>
                                  : <Badge colorScheme="gray">Normal</Badge>}
                              </Td>
                              <Td>
                                <Badge colorScheme={cfg.is_active ? 'green' : 'gray'}>
                                  {cfg.is_active ? 'Aktif' : 'Nonaktif'}
                                </Badge>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <IconButton aria-label="Edit" icon={<FiEdit />} size="xs" variant="ghost" onClick={() => handleEditConfig(cfg)} />
                                  <IconButton aria-label="Hapus" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDeleteConfig(cfg.id)} />
                                </HStack>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>

                  {/* Feature Flags */}
                  <TabPanel p={4}>
                    <HStack justify="flex-end" mb={3}>
                      <Button leftIcon={<FiPlus />} colorScheme="green" size="sm" onClick={handleOpenCreateFlag}>
                        Tambah Flag
                      </Button>
                    </HStack>
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Flag Key</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Company</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Estate</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Enabled</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Value</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Status</Th>
                            <Th fontSize={SAKTI_UI_SIZE.tableHeaderFont}>Aksi</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {loading ? (
                            <Tr><Td colSpan={7} textAlign="center" py={8}><Spinner size="sm" /></Td></Tr>
                          ) : featureFlags.length === 0 ? (
                            <Tr><Td colSpan={7} textAlign="center" py={8} color="gray.400" fontSize="sm">Belum ada feature flags</Td></Tr>
                          ) : featureFlags.map(flag => (
                            <Tr key={flag.id}>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont} fontWeight="medium">{flag.flag_key}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{flag.company_id ?? '—'}</Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont}>{flag.estate_id ?? '—'}</Td>
                              <Td>
                                <Badge colorScheme={flag.is_enabled ? 'blue' : 'gray'}>
                                  {flag.is_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                              </Td>
                              <Td fontSize={SAKTI_UI_SIZE.tableCellFont} maxW="160px">
                                {flag.value != null
                                  ? <Text as="code" fontSize="xs" color="gray.600" isTruncated display="block">{JSON.stringify(flag.value)}</Text>
                                  : '—'}
                              </Td>
                              <Td>
                                <Badge colorScheme={flag.is_active ? 'green' : 'gray'}>
                                  {flag.is_active ? 'Aktif' : 'Nonaktif'}
                                </Badge>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <IconButton aria-label="Edit" icon={<FiEdit />} size="xs" variant="ghost" onClick={() => handleEditFlag(flag)} />
                                  <IconButton aria-label="Hapus" icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDeleteFlag(flag.id)} />
                                </HStack>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </VStack>
        </Box>
      </Flex>

      <AppConfigFormModal
        isOpen={isConfigOpen}
        onClose={onConfigClose}
        item={editConfig}
        onSave={handleSaveConfig}
        saving={savingConfig}
      />
      <FeatureFlagFormModal
        isOpen={isFlagOpen}
        onClose={onFlagClose}
        item={editFlag}
        onSave={handleSaveFlag}
        saving={savingFlag}
      />
    </Box>
  );
};

export default SaktiAppConfigPage;
